/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/VoiceGuidesActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Actions column renderer for Voice Guides DataGrid.
    Uses Extensions-style icon buttons for Edit and Delete actions.
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { VoiceGuide } from '../types';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { deleteVoiceGuide } from '../api/voiceGuidesApi';

interface VoiceGuidesActionsProps {
  item: VoiceGuide;
  onEdit?: (item: VoiceGuide) => void;
  onDeleted?: () => void;
}

/**
 * Render actions column for Voice Guide item
 * Uses Extensions-style icon buttons: Edit (blue), Delete (red)
 */
export function VoiceGuidesActions({ item, onEdit, onDeleted }: VoiceGuidesActionsProps) {
  const { t } = useI18n();
  const { addNotification } = useNotifications();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // System guides cannot be deleted (is_system = true means protected)
  const canDelete = !item.is_system;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteVoiceGuide(item.id);
      addNotification({
        type: 'success',
        message: t('voiceGuides:actions.deleteSuccess') || 'Voice guide deleted successfully'
      });
      onDeleted?.();
    } catch (err) {
      console.error('Failed to delete voice guide:', err);
      addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : (t('voiceGuides:actions.deleteError') || 'Failed to delete voice guide')
      });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Edit Button - always visible */}
      <SimpleTooltip content={t('common:edit') || 'Edit'} position="top">
        <button
          onClick={() => onEdit?.(item)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>

      {/* Delete Button - only for custom guides */}
      {canDelete && (
        <>
          <SimpleTooltip content={t('common:delete') || 'Delete'} position="top">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleting}
              className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
            </button>
          </SimpleTooltip>

          <ConfirmationModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            title={t('voiceGuides:actions.deleteConfirmTitle') || 'Delete Voice Guide'}
            message={
              t('voiceGuides:actions.deleteConfirmDescription', { name: item.name }) ||
              `Are you sure you want to delete "${item.name}"? This will also delete all associated audio files. This action cannot be undone.`
            }
            confirmText={t('common:delete') || 'Delete'}
            cancelText={t('common:cancel') || 'Cancel'}
            onConfirm={handleDelete}
            isLoading={deleting}
            type="danger"
          />
        </>
      )}
    </div>
  );
}
